import dayjs from 'dayjs'
import { storeToRefs } from 'pinia'

import { useStoreSettingsNotifications } from '@/entities/notification'
import { useStoreRooms } from '@/entities/room'
import { useRoomScheduleStore } from '@/entities/schedule'
import { useNotification } from '@/entities/use-notification'

import { useLocalization } from '@/shared/composables/localization'
import {
  ScheduleReminderOffset,
  type ScheduleReminderOffsetValue,
} from '@/shared/types/notification'
import { getScheduleEventDisplayName } from '@/shared/utils/schedule'

import {
  MAX_SCHEDULE_REMINDER_OFFSET_SECONDS,
  SCHEDULE_REMINDER_OFFSET_SECONDS,
} from '../lib/offsets'
import { getUpcomingScheduleStarts } from '../lib/upcoming-starts'

const POLL_INTERVAL_MS = 30_000
/** Небольшой запас к max offset, чтобы не пропустить тик на границе окна. */
const LOOKAHEAD_PADDING_SECONDS = 90
/**
 * Насколько поздно ещё можно показать напоминание после fireAt.
 * Ограничение 5 мин: «за 1 час» не всплывает, если клиент открыли уже за пару минут до старта.
 */
const MAX_FIRE_GRACE_SECONDS = 5 * 60

let pollTimer: ReturnType<typeof setInterval> | null = null
const firedKeys = new Set<string>()

const buildFiredKey = (
  pServed: string,
  eventUid: string,
  occurrenceStart: number,
  offset: ScheduleReminderOffsetValue,
) => `${pServed}|${eventUid}|${occurrenceStart}|${offset}`

/** Ключ: pServed|uid|occurrenceStart|offset — occurrenceStart перед последним сегментом. */
const getOccurrenceStartFromFiredKey = (key: string): number | undefined => {
  const lastSep = key.lastIndexOf('|')
  if (lastSep <= 0) {
    return undefined
  }
  const secondLastSep = key.lastIndexOf('|', lastSep - 1)
  if (secondLastSep < 0) {
    return undefined
  }
  const occurrenceStart = Number(key.slice(secondLastSep + 1, lastSep))
  return Number.isFinite(occurrenceStart) ? occurrenceStart : undefined
}

const pruneFiredKeys = (nowUnix: number) => {
  firedKeys.forEach((key) => {
    const occurrenceStart = getOccurrenceStartFromFiredKey(key)
    if (occurrenceStart === undefined || occurrenceStart < nowUnix) {
      firedKeys.delete(key)
    }
  })
}

const tick = () => {
  const nowUnix = dayjs().unix()
  pruneFiredKeys(nowUnix)

  const { scheduleReminderOffsets } = storeToRefs(useStoreSettingsNotifications())
  const offsets = scheduleReminderOffsets.value
  if (!offsets.length) {
    return
  }

  const { scheduleByRoomPServed } = storeToRefs(useRoomScheduleStore())
  const { findByPServed } = useStoreRooms()
  const { createScheduleReminderNotification } = useNotification()
  const { t } = useLocalization()

  const untilUnix = nowUnix + MAX_SCHEDULE_REMINDER_OFFSET_SECONDS + LOOKAHEAD_PADDING_SECONDS

  Object.entries(scheduleByRoomPServed.value).forEach(([pServed, events]) => {
    const room = findByPServed(pServed)
    events?.forEach((event) => {
      if (!event.uid) {
        return
      }

      const starts = getUpcomingScheduleStarts(event, nowUnix, untilUnix)
      starts.forEach((occurrenceStart) => {
        offsets.forEach((offset) => {
          const offsetSeconds = SCHEDULE_REMINDER_OFFSET_SECONDS[offset]
          if (!offsetSeconds) {
            return
          }

          const fireAt = occurrenceStart - offsetSeconds
          if (nowUnix < fireAt || nowUnix >= occurrenceStart) {
            return
          }

          const graceSeconds = Math.min(offsetSeconds, MAX_FIRE_GRACE_SECONDS)
          if (nowUnix - fireAt > graceSeconds) {
            return
          }

          const key = buildFiredKey(pServed, event.uid, occurrenceStart, offset)
          if (firedKeys.has(key)) {
            return
          }

          firedKeys.add(key)
          createScheduleReminderNotification({
            pServed,
            eventUid: event.uid,
            eventTitle: getScheduleEventDisplayName(event.summary, t('common.withoutTitle')),
            occurrenceStart,
            offset,
            ...(room?.id ? { roomId: room.id } : {}),
            ...(room?.name ? { roomName: room.name } : {}),
          })
        })
      })
    })
  })
}

const onVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    tick()
  }
}

export const startScheduleReminders = () => {
  if (pollTimer) {
    return
  }

  tick()
  pollTimer = setInterval(tick, POLL_INTERVAL_MS)
  document.addEventListener('visibilitychange', onVisibilityChange)
}

export const stopScheduleReminders = () => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
  document.removeEventListener('visibilitychange', onVisibilityChange)
  firedKeys.clear()
}

/** Test-only helpers */
export const __scheduleRemindersTestUtils = {
  tick,
  firedKeys,
  buildFiredKey,
  ScheduleReminderOffset,
}
